
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import api from '../service/api';
import PasswordChangedDialog from '@/components/PasswordChangedDialog.vue';

@Component({
  components: {
    PasswordChangedDialog,
  }
})
export default class ResetPassword extends Vue {
    get repeatPasswordRules() {
        return [
            (v: string) => !!v || 'Hasło jest wymagane',
            (v: string) => v === this.password || 'Hasła muszą być identyczne',
        ];
    }
    @Prop({
        type: String,
        required: true,
    })
    code!: string;
    password: string = '';
    repeatPassword: string = '';

    passwordChanged = false;

    passwordRules = [
        (v: string) => !!v || 'Hasło jest wymagane',
        (v: string) => /((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))/.exec(v) || 'Hasło musi mieć minimum 6 znaków, w tym dużą litere i cyfrę.',
    ];

    // Starfield properties
    private starfieldAnimation: number | null = null;
    private stars: Array<{x: number, y: number, size: number, speed: number, opacity: number, twinkle: number}> = [];
    private doodles: Array<{x: number, y: number, type: string, size: number, speed: number, rotation: number, opacity: number}> = [];
    private comets: Array<{x: number, y: number, vx: number, vy: number, size: number, opacity: number, trail: Array<{x: number, y: number, opacity: number}>, life: number, maxLife: number}> = [];

    mounted() {
        this.$nextTick(() => {
            this.initStarfield();
            window.addEventListener('resize', this.handleResize);
        });
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        if (this.starfieldAnimation) {
            cancelAnimationFrame(this.starfieldAnimation);
        }
    }

    handleResize() {
        const canvas = this.$refs.starfield as HTMLCanvasElement;
        if (canvas) {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        }
    }

    async submit() {
        console.log({ valid: (this.$refs.form as any).validate() });
        if ((this.$refs.form as any).validate()) {
            await api.resetPassword(this.code, this.password);
            this.passwordChanged = true;
            setTimeout(() => {
                this.$router.push({
                    name: 'home',
                });
            }, 2000);
        }
    }

    // Starfield methods
    private initStarfield() {
        const canvas = this.$refs.starfield as HTMLCanvasElement;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        // Generate stars
        this.stars = [];
        const numStars = Math.floor((canvas.width * canvas.height) / 10000);

        for (let i = 0; i < numStars; i++) {
            this.stars.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 2 + 0.5,
                speed: Math.random() * 0.5 + 0.1,
                opacity: Math.random() * 0.8 + 0.2,
                twinkle: Math.random() * Math.PI * 2
            });
        }

        // Generate doodles
        this.doodles = [];
        const numDoodles = Math.floor((canvas.width * canvas.height) / 50000);
        const doodleTypes = ['circle', 'square', 'triangle', 'star', 'heart'];

        for (let i = 0; i < numDoodles; i++) {
            this.doodles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                type: doodleTypes[Math.floor(Math.random() * doodleTypes.length)],
                size: Math.random() * 8 + 4,
                speed: Math.random() * 0.3 + 0.1,
                rotation: Math.random() * Math.PI * 2,
                opacity: Math.random() * 0.6 + 0.2
            });
        }

        // Initialize comets array
        this.comets = [];

        this.animateStarfield();
    }

    private animateStarfield() {
        const canvas = this.$refs.starfield as HTMLCanvasElement;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas with #030303 background
        ctx.fillStyle = '#030303';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Randomly spawn comets
        if (Math.random() < 0.005) {
            this.spawnComet();
        }

        // Update and draw comets
        this.comets = this.comets.filter(comet => {
            // Update position
            comet.x += comet.vx;
            comet.y += comet.vy;
            comet.life++;

            // Add trail point
            comet.trail.push({ x: comet.x, y: comet.y, opacity: 1 });
            
            // Limit trail length
            if (comet.trail.length > 20) {
                comet.trail.shift();
            }

            // Fade trail
            comet.trail.forEach(point => {
                point.opacity *= 0.95;
            });

            // Draw pixelated trail
            for (let i = 0; i < comet.trail.length - 1; i++) {
                const point = comet.trail[i];
                const nextPoint = comet.trail[i + 1];
                
                const dx = nextPoint.x - point.x;
                const dy = nextPoint.y - point.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const steps = Math.max(1, Math.floor(distance / 2));
                
                for (let j = 0; j <= steps; j++) {
                    const t = j / steps;
                    const trailX = Math.floor(point.x + dx * t);
                    const trailY = Math.floor(point.y + dy * t);
                    
                    ctx.fillStyle = `rgba(255, 255, 255, ${point.opacity * 0.3})`;
                    ctx.fillRect(trailX, trailY, 2, 2);
                }
            }

            // Draw pixelated comet head
            const pixelSize = Math.max(2, Math.floor(comet.size));
            const halfSize = Math.floor(pixelSize / 2);
            
            ctx.fillStyle = `rgba(255, 255, 255, ${comet.opacity})`;
            ctx.fillRect(
                Math.floor(comet.x - halfSize), 
                Math.floor(comet.y - halfSize), 
                pixelSize, 
                pixelSize
            );

            // Add pixelated glow effect
            const glowSize = Math.max(4, Math.floor(comet.size * 2));
            const glowHalfSize = Math.floor(glowSize / 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${comet.opacity * 0.2})`;
            ctx.fillRect(
                Math.floor(comet.x - glowHalfSize), 
                Math.floor(comet.y - glowHalfSize), 
                glowSize, 
                glowSize
            );

            // Remove if off screen or life expired
            return comet.life < comet.maxLife && 
                   comet.x > -50 && comet.x < canvas.width + 50 && 
                   comet.y > -50 && comet.y < canvas.height + 50;
        });

        // Draw stars
        this.stars.forEach(star => {
            // Update twinkle
            star.twinkle += 0.02;
            const twinkleOpacity = star.opacity * (0.5 + 0.5 * Math.sin(star.twinkle));

            // Draw pixelated star
            const pixelSize = Math.max(1, Math.floor(star.size));
            const halfSize = Math.floor(pixelSize / 2);
            
            ctx.fillStyle = `rgba(255, 255, 255, ${twinkleOpacity})`;
            ctx.fillRect(
                Math.floor(star.x - halfSize), 
                Math.floor(star.y - halfSize), 
                pixelSize, 
                pixelSize
            );

            // Add pixelated glow effect
            const glowSize = Math.max(2, Math.floor(star.size * 1.5));
            const glowHalfSize = Math.floor(glowSize / 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${twinkleOpacity * 0.3})`;
            ctx.fillRect(
                Math.floor(star.x - glowHalfSize), 
                Math.floor(star.y - glowHalfSize), 
                glowSize, 
                glowSize
            );

            // Move star
            star.y -= star.speed;
            if (star.y < -50) {
                star.y = canvas.height + 50;
                star.x = Math.random() * canvas.width;
            }
        });

        // Draw doodles
        this.doodles.forEach(doodle => {
            ctx.save();
            ctx.translate(doodle.x, doodle.y);
            ctx.rotate(doodle.rotation);
            ctx.globalAlpha = doodle.opacity;
            
            this.drawDoodle(ctx, doodle.type, doodle.size);
            
            ctx.restore();

            // Move doodle
            doodle.y -= doodle.speed;
            doodle.rotation += 0.01;
            if (doodle.y < -50) {
                doodle.y = canvas.height + 50;
                doodle.x = Math.random() * canvas.width;
                doodle.rotation = Math.random() * Math.PI * 2;
            }
        });

        this.starfieldAnimation = requestAnimationFrame(() => this.animateStarfield());
    }

    private drawDoodle(ctx: CanvasRenderingContext2D, type: string, size: number) {
        const halfSize = size / 2;
        
        switch (type) {
            case 'circle':
                ctx.beginPath();
                ctx.arc(0, 0, halfSize, 0, Math.PI * 2);
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
                
            case 'square':
                ctx.fillStyle = '#ff4081';
                ctx.fillRect(-halfSize, -halfSize, size, size);
                break;
                
            case 'triangle':
                ctx.beginPath();
                ctx.moveTo(0, -halfSize);
                ctx.lineTo(-halfSize, halfSize);
                ctx.lineTo(halfSize, halfSize);
                ctx.closePath();
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
                
            case 'star':
                ctx.beginPath();
                for (let i = 0; i < 5; i++) {
                    const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
                    const x = Math.cos(angle) * halfSize;
                    const y = Math.sin(angle) * halfSize;
                    if (i === 0) { ctx.moveTo(x, y); } else { ctx.lineTo(x, y); }
                    
                    const innerAngle = angle + Math.PI / 5;
                    const innerX = Math.cos(innerAngle) * (halfSize * 0.4);
                    const innerY = Math.sin(innerAngle) * (halfSize * 0.4);
                    ctx.lineTo(innerX, innerY);
                }
                ctx.closePath();
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
                
            case 'heart':
                ctx.beginPath();
                ctx.moveTo(0, halfSize * 0.3);
                ctx.bezierCurveTo(
                    -halfSize * 0.5, -halfSize * 0.2,
                    -halfSize, -halfSize * 0.5,
                    0, -halfSize
                );
                ctx.bezierCurveTo(
                    halfSize, -halfSize * 0.5,
                    halfSize * 0.5, -halfSize * 0.2,
                    0, halfSize * 0.3
                );
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
        }
    }

    private spawnComet() {
        const canvas = this.$refs.starfield as HTMLCanvasElement;
        if (!canvas) return;

        const side = Math.floor(Math.random() * 4);
        let x = 0, y = 0, vx = 0, vy = 0;
        
        switch (side) {
            case 0: // top
                x = Math.random() * canvas.width;
                y = -20;
                vx = (Math.random() - 0.5) * 2;
                vy = Math.random() * 1 + 0.5;
                break;
            case 1: // right
                x = canvas.width + 20;
                y = Math.random() * canvas.height;
                vx = -(Math.random() * 1 + 0.5);
                vy = (Math.random() - 0.5) * 2;
                break;
            case 2: // bottom
                x = Math.random() * canvas.width;
                y = canvas.height + 20;
                vx = (Math.random() - 0.5) * 2;
                vy = -(Math.random() * 1 + 0.5);
                break;
            case 3: // left
                x = -20;
                y = Math.random() * canvas.height;
                vx = Math.random() * 1 + 0.5;
                vy = (Math.random() - 0.5) * 2;
                break;
        }

        const comet = {
            x,
            y,
            vx,
            vy,
            size: Math.random() * 3 + 2,
            opacity: 1,
            trail: [],
            life: 0,
            maxLife: Math.random() * 200 + 100
        };

        this.comets.push(comet);
    }
}
